import { useEffect, useState } from 'react';

/**
 * Custom hook to manage delayed rendering for smooth modal transitions
 * Keeps component mounted briefly after closing to prevent flicker
 *
 * @param isOpen - Whether the component should be visible
 * @param delay - Delay in milliseconds before unmounting (default: 50ms)
 * @returns shouldRender - Whether the component should be rendered in DOM
 */
const useDelayedRender = (isOpen: boolean, delay = 50): boolean => {
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      return undefined;
    }

    // When closing, delay the unmounting
    const timer = setTimeout(() => {
      setShouldRender(false);
    }, delay);

    // Return cleanup function to clear timeout
    return () => {
      clearTimeout(timer);
    };
  }, [isOpen, delay]);

  return shouldRender;
};

export default useDelayedRender;
